import type { EnvironmentRunner } from "@/lib/api";
import { RUNNER_UPDATE_REQUIRED_REASON } from "@/lib/runner";

const BADGE_BASE: React.CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	flexShrink: 0,
	height: 20,
	padding: "0 8px",
	borderRadius: "var(--radius-full)",
	fontSize: "var(--text-caption)",
	fontWeight: 500,
	whiteSpace: "nowrap",
};

const WARNING_BADGE: React.CSSProperties = {
	...BADGE_BASE,
	background: "var(--warning-subtle)",
	color: "var(--warning)",
};

/** デバイスの名前はカードの幅を超えうるので省略し、全体は title で見せる。 */
const NAME_MAX_WIDTH = 200;

/**
 * デバイスで動く環境の実行場所 (「デバイス: <名前>」) と、デバイスがオフラインなら「オフライン」、
 * オンラインでランナーの更新が必要なら「デバイスの更新が必要」を出す。クラウドの環境では何も出さない。
 * 更新の案内は、デバイスを更新できる人 (canUpdateDevice) とそれ以外で、すべきことを分ける。
 */
export function EnvironmentLocationBadge({
	runner,
	canUpdateDevice,
}: {
	runner: EnvironmentRunner | null;
	canUpdateDevice: boolean;
}) {
	if (!runner) return null;
	const label = `デバイス: ${runner.name}`;
	return (
		<span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
			<span
				data-testid="environment-location-badge"
				title={label}
				data-tooltip-if-truncated
				style={{
					...BADGE_BASE,
					// 並ぶタグやボタンに重ならないよう、行の幅が足りなければ縮んで名前を省略する。
					flexShrink: 1,
					minWidth: 0,
					maxWidth: NAME_MAX_WIDTH,
					background: "var(--surface-2)",
					color: "var(--fg-2)",
				}}
			>
				<span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
			</span>
			{!runner.online && (
				<span data-testid="environment-location-offline" style={WARNING_BADGE}>
					オフライン
				</span>
			)}
			{runner.online && runner.updateRequired && (
				<span
					data-testid="environment-location-update-required"
					title={`${RUNNER_UPDATE_REQUIRED_REASON}${
						canUpdateDevice
							? "サイドバーの「デバイス」から更新してください。"
							: "組織の管理者にデバイスの更新を頼んでください。"
					}`}
					style={WARNING_BADGE}
				>
					デバイスの更新が必要
				</span>
			)}
		</span>
	);
}
